<script lang="ts">
	import { createQuery } from '@tanstack/svelte-query';
	import { untrack } from 'svelte';
	import { writable } from 'svelte/store';
	import type { getExploreSection } from '../../utils/getExploreSection/getExploreSection';
	import { getSectionPriority } from '../../utils/getSectionPriority/getSectionPriority';
	import type { getSections } from '../../utils/getSections/getSections';
	import { getVariables } from '../../utils/getVariables/getVariables';
	import { newestFirst } from '../../utils/newestFirst/newestFirst';
	import { FeatureFlags } from '#lib/constants/feature-flags.js';
	import { GlobalRoutes } from '#lib/features/navigation/constants/global-routes.js';
	import type { SearchGlobalQuery } from '#lib/features/search/global/queries/__generated__/searchGlobal.generated.js';
	import {
		getArtworksByCollectionSlug,
		PRIMARY_COLLECTION_SLOTS,
	} from '#lib/features/search/global/utils/primaryCollectionArtworks/primaryCollectionArtworks.js';
	import { goto } from '$app/navigation';
	import { page } from '$app/state';
	import { ChevronRightIcon } from '$global/assets/icons/ChevronRightIcon';
	import { QuerySearch } from '$global/features/search/components/QuerySearch';
	import type { SearchCombobox } from '$global/features/search/utils/createSearchCombobox';
	import { track } from '$global/utils/track';

	interface Props {
		onCloseSearch?: (() => void) | undefined;
		combobox: SearchCombobox | undefined;
		class?: string;
	}

	let {
		onCloseSearch = undefined,
		combobox,
		class: className = undefined,
	}: Props = $props();

	// The section being browsed surfaces its own results first.
	let priority = $derived(getSectionPriority(page.url.pathname));

	// Primary_Collection has no artworks relation, so a matched collection's
	// works need a second query keyed by the slugs the first one returned.
	// It's dependent on the first query by nature; TanStack caches per slug set,
	// so repeat searches and backspacing don't refetch.
	//
	// getSections is called from inside QuerySearch's $derived, and Svelte 5
	// forbids updating reactive state during derivation (state_unsafe_mutation).
	// That applies to a store here as much as to a $state rune, because
	// createQuery subscribes to it: writing synchronously re-enters the same
	// reactive pass. So the write is deferred to a microtask, landing after the
	// current render, and guarded so an unchanged slug set is never re-published
	// (which would loop).
	const primaryCollectionSlugs = writable<string[]>([]);
	// The search term is carried alongside the slugs so the artwork previews can
	// be ranked by it; it is not part of the query filter, only of the ordering.
	const primarySearchValue = writable('');
	let queuedSlugKey = '';

	const requestArtworksFor = (slugs: string[], searchValue: string) => {
		const key = `${searchValue}::${slugs.join('|')}`;
		if (key === queuedSlugKey) return;

		queuedSlugKey = key;
		queueMicrotask(() => {
			// Untracked too: Firefox Mobile has run this microtask while a derived
			// was still active (Sentry #3029), and the write is never meant to be
			// part of any reaction.
			untrack(() => {
				primarySearchValue.set(searchValue);
				primaryCollectionSlugs.set(slugs);
			});
		});
	};

	// The REST bodies mirror the two documents' data. Their integer ids are
	// GraphQL strings on the other path, and nothing here string-compares one
	// (getSections sorts sub-series with Number(id)), so the bodies are cast to
	// the document types rather than the section utilities re-typed.
	const isRest = FeatureFlags.RestPages['GlobalSearch (global header search)'];

	// The search requests (REST or GraphQL, see searchGlobal) are imported on
	// first use, keeping the GraphQL client, the `graphql` runtime and the query
	// documents out of every page's preloaded scripts. Focusing the input starts
	// the import, so it is normally done before the debounced first request.
	const loadSearchGlobal = () =>
		import('../../utils/searchGlobal/searchGlobal');
	const loadSearchPrimaryCollectionArtworks = () =>
		import('../../utils/searchPrimaryCollectionArtworks/searchPrimaryCollectionArtworks');

	// The section builders (and the explore-search table) only run on results,
	// so they load with the request: data can never arrive before them.
	let sectionBuilders = $state<{
		getSections: typeof getSections;
		getExploreSection: typeof getExploreSection;
	}>();

	const loadSectionBuilders = async () => {
		if (sectionBuilders) return;

		const [sectionsModule, exploreModule] = await Promise.all([
			import('../../utils/getSections/getSections'),
			import('../../utils/getExploreSection/getExploreSection'),
		]);
		sectionBuilders = {
			getSections: sectionsModule.getSections,
			getExploreSection: exploreModule.getExploreSection,
		};
	};

	const prefetchSearch = () => {
		void loadSearchGlobal();
		void loadSearchPrimaryCollectionArtworks();
		void loadSectionBuilders();
	};

	const requestSearch = async (searchValue: string) => {
		const [{ searchGlobal }] = await Promise.all([
			loadSearchGlobal(),
			loadSectionBuilders(),
		]);

		return searchGlobal(searchValue);
	};

	const artworksQuery = createQuery(() => ({
		queryKey: ['primary-collection-artworks', ...$primaryCollectionSlugs],
		enabled: $primaryCollectionSlugs.length > 0,
		queryFn: async () =>
			(
				await loadSearchPrimaryCollectionArtworks()
			).searchPrimaryCollectionArtworks($primaryCollectionSlugs),
	}));

	let artworksByCollectionSlug = $derived(
		getArtworksByCollectionSlug(artworksQuery.data, $primarySearchValue)
	);

	let getPrioritizedSections = $derived(
		(rawData: SearchGlobalQuery | undefined, searchValue = '') => {
			// The endpoint orders collections by relevance, so its order is kept.
			// The document's `-release_date` sort puts a collection with no
			// release date first; the dropdown dates it by its first drop
			// instead, like the results page (see newestFirst).
			const data =
				rawData &&
				(isRest
					? rawData
					: {
							...rawData,
							Primary_Collection: newestFirst(rawData.Primary_Collection || []),
						});

			// Feed the slugs from this render's results back into the second query.
			const slugs = (data?.Primary_Collection || [])
				.map((collection) => collection?.slug)
				.filter((slug): slug is string => Boolean(slug))
				.slice(0, PRIMARY_COLLECTION_SLOTS);

			// The raw search term isn't handed to getSections, but the artist it
			// matched is: ranking only needs to know which artist to promote, and
			// a matched Artists row names them exactly (no _icontains guesswork).
			// No artist match means no reordering, which is the wanted behaviour
			// for collection-name searches.
			requestArtworksFor(slugs, data?.Artists?.[0]?.name || '');

			if (!sectionBuilders) return [];
			const { getSections, getExploreSection } = sectionBuilders;

			// The term is passed for ranking only — the filters already ran
			// server-side — so a series or collection the visitor named by title
			// leads its section instead of being outranked by newer artworks that
			// merely contain the word.
			const sections = getSections(
				data,
				priority,
				artworksByCollectionSlug,
				searchValue
			);

			// A term naming a platform/page (or one of its aliases, e.g. "print"
			// → Editions) links straight there, above the data-backed sections.
			const exploreSection = getExploreSection(searchValue);

			return exploreSection ? [exploreSection, ...sections] : sections;
		}
	);

	const dataCy = 'global-search';

	const getSeeAllHref = (searchValue: string) =>
		`${GlobalRoutes.Search}?searchValue=${encodeURIComponent(searchValue)}`;

	export const getRequestError = (error?: string) => {
		if (error) {
			return 'Something went wrong during the search. Please try again later.';
		}
		return null;
	};
</script>

<div class="contents" onfocusin={prefetchSearch}>
	<QuerySearch
		{dataCy}
		{combobox}
		{onCloseSearch}
		class={className}
		placeholder="Search artists, artworks and more..."
		ariaLabel="Search HENI"
		minSearchLength={2}
		classes={{
			sectionTitle: 'mt-0 lg:mt-0',
			// Containerize the mobile result rows only: their grounds (group-header
			// tint, accent rule, dividers) otherwise run edge-to-edge on small
			// screens. Same horizontal padding as the shared Container component;
			// section titles, the see-all header and the EXPLORE page links keep
			// the full-bleed layout.
			sectionResults: (section) =>
				section.title === 'EXPLORE' ? '' : 'px-4 sm:px-6 lg:px-0',
		}}
		emptyValueResponse={{
			Artists: [],
			Editions: [],
			Series: [],
			Primary_Artworks: [],
			Primary_Collection: [],
			Publishing_Books: [],
			Merch_Item: [],
			Shop_Collection: [],
			HENI_Talks_Talks: [],
			HENI_Talks_Podcast: [],
			NFT_Collection: [],
			NFT_Tokens: [],
		}}
		getError={getRequestError}
		getSections={getPrioritizedSections}
		{getVariables}
		request={requestSearch}
		mixpanelPrefix="GLOBAL"
		{track}
		viewAllResultsHref={GlobalRoutes.Search}
		goto={(href) => goto(href)}
		onEnter={(searchValue) => goto(getSeeAllHref(searchValue))}
	>
		{#snippet renderHeader({ searchValue, onCloseSearch })}
			<!--
			The dropdown caps each section at 4-5 results; this row (and the
			per-section "See all" links) hands over to the full /search page.
		-->
			<a
				data-cy="global-search-see-all-results"
				href={getSeeAllHref(searchValue)}
				onclick={onCloseSearch}
				class="flex items-center gap-1 border-b border-gray-100 px-6 pt-1 pb-3 text-[0.8125rem] font-medium tracking-[.08em] text-gray-700 uppercase hover:text-gray-900"
			>
				See all results for “{searchValue}...”
				<ChevronRightIcon class="h-3.5 w-3.5" />
			</a>
		{/snippet}
	</QuerySearch>
</div>
